/** Шапка Aurora (ScreenOverview прототипа): стеклянная капсула — лого-квадрат
 *  path-градиента, строка «БРЕНД · живая карта / офлайн», имя площадки; далее
 *  кнопка поиска и «Объём», отдельная капсула языков RU|EN|ET. QR всей
 *  территории живёт в рельсе инструментов: в шапке прототипа одно действие,
 *  и длинному имени площадки нужна вся ширина капсулы. */

import { BRAND } from '@app/brand'
import { getLang, LANGS, type Lang, setLang, tr } from '@app/i18n'
import { localized } from '@app/rvf'
import { useEffect, useRef, useState } from 'preact/hooks'
import { isOffline, langTick, searchOpen, venue, venueVerified } from '../state'
import { GLYPH_SEARCH } from './glyphs'
import { VolumeSwitch } from './volume-switch'

const LANG_NAME_KEY = {
  ru: 'lang.name_ru',
  en: 'lang.name_en',
  et: 'lang.name_et',
} as const

/** Выпадающее меню языков: видны ВСЕ варианты (вместо кнопки-цикла). */
/** Языковое меню; экспорт — используется и в аварийном режиме (26.07). */
export function LangMenu() {
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('pointerdown', onDown)
    return () => window.removeEventListener('pointerdown', onDown)
  }, [open])
  const cur = getLang()
  return (
    <div ref={boxRef} class="lang-wrap">
      <button
        type="button"
        class={`lang-btn${open ? ' lang-btn-on' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {tr('lang.self')} <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div class="lang-menu glass" role="listbox">
          {LANGS.map((l) => (
            <button
              key={l}
              type="button"
              role="option"
              aria-selected={l === cur}
              class={`lang-item${l === cur ? ' lang-item-on' : ''}`}
              onClick={() => {
                setLang(l)
                setOpen(false)
              }}
            >
              <span>{tr(LANG_NAME_KEY[l as keyof typeof LANG_NAME_KEY] ?? 'lang.self')}</span>
              {l === cur && <span aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/** Знак бренда в квадрате path-градиента (IconLogo прототипа — «дорожка»). */
function BrandMark() {
  return (
    <span class="hdr-logo" aria-hidden="true">
      <svg
        viewBox="0 0 40 40"
        width="20"
        height="20"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M6 30c4-14 14-22 28-24-2 14-10 24-24 28-1 .3-4-3-4-4Z"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linejoin="round"
        />
        <circle cx="15" cy="25" r="2.6" fill="currentColor" />
      </svg>
    </span>
  )
}

/** Капсула языков RU|EN|ET с разделителями — все варианты сразу, без меню. */
function LangCapsule() {
  const cur = getLang()
  return (
    <div class="hdr-langs glass">
      {/* Разделители между кнопками рисует CSS (.hdr-lang + .hdr-lang::before):
          отдельные элементы-палочки потребовали бы ключей на фрагментах. */}
      {LANGS.map((l) => (
        <button
          key={l}
          type="button"
          class={`hdr-lang${l === cur ? ' hdr-lang-on' : ''}`}
          aria-pressed={l === cur}
          aria-label={tr(LANG_NAME_KEY[l as keyof typeof LANG_NAME_KEY] ?? 'lang.self')}
          onClick={() => setLang(l as Lang)}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  )
}

export function Header() {
  langTick.value
  const v = venue.value
  if (!v) return null
  const name = localized(v.venue.name, getLang(), v.venue.default_language)
  const offline = isOffline.value

  return (
    <header class="hdr">
      <div class="hdr-brand glass">
        <BrandMark />
        <div class="hdr-text">
          <div class="hdr-brandrow">
            <span class="hdr-brandname">{BRAND.productName}</span>
            {!offline && <span class="hdr-livedot" aria-hidden="true" />}
            <span class={`hdr-status${offline ? ' hdr-status-off' : ''}`}>
              {tr(offline ? 'hdr.offline' : 'hdr.live')}
            </span>
          </div>
          <h1 class="hdr-title" title={name}>
            {name}
            {venueVerified.value && (
              <span class="hdr-verified" title={tr('dir.verified')}>
                ✓
              </span>
            )}
          </h1>
        </div>
        <button
          type="button"
          class="hdr-iconbtn"
          aria-label={tr('search.open')}
          onClick={() => {
            searchOpen.value = true
          }}
        >
          <span
            class="glyph"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: GLYPH_SEARCH }}
          />
        </button>
      </div>
      {/* Объёмные виды — вне капсулы: этот же переключатель возвращает на
          карту из режима «Объём», где капсула площадки не показывается. */}
      <VolumeSwitch />
      <LangCapsule />
    </header>
  )
}
